import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Calendar, Eye } from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import NotFound from "@/pages/NotFound";
import { fetchBlogPost } from "@/lib/cmsApi";
import { adminApi, getAdminToken } from "@/lib/adminApi";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-IE", { day: "numeric", month: "long", year: "numeric" }); }
  catch (e) { return iso; }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const previewMode = searchParams.get("preview") === "1" && !!getAdminToken();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true); setNotFound(false);
    const loader = previewMode
      ? adminApi.get(`/admin/cms/preview/blog/${slug}`).then((r) => r.data)
      : fetchBlogPost(slug);
    loader.then((d) => {
      setPost(d);
      document.title = `${d.title} · AGS Services${previewMode ? " · PREVIEW" : ""}`;
    }).catch((err) => { if (err?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug, previewMode]);

  if (loading) return <div className="min-h-screen bg-white" />;
  if (notFound || !post) return <NotFound />;

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A] antialiased">
      <Navigation />
      {previewMode && (
        <div data-testid="preview-banner" className="bg-[#FFB836] text-[#050B14] px-4 py-3 text-center font-mono text-[11px] uppercase tracking-[0.22em] flex items-center justify-center gap-2 sticky top-0 z-40">
          <Eye size={12} /> Draft preview — not visible to the public
        </div>
      )}
      <main>
        {/* Hero */}
        <section className="bg-[#F4F8FC] pt-40 pb-16 border-b border-[#E5EBF2]">
          <div className="container-ags max-w-4xl mx-auto">
            <Link to="/blog" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57] hover:text-[#2BB6E5] mb-8">
              <ArrowLeft size={12} /> All articles
            </Link>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#2BB6E5] flex items-center gap-2">
              <Calendar size={11} /> <span>{fmtDate(post.published_at)}</span>
              {(post.tags || []).length > 0 && (
                <>
                  <span className="h-px w-6 bg-[#2BB6E5]/40" />
                  <span>{post.tags.join(" · ")}</span>
                </>
              )}
            </div>
            <h1 className="mt-5 font-heading font-extrabold text-4xl md:text-6xl tracking-[-0.04em] text-[#050B14] leading-[1.02]">
              {post.title}
            </h1>
            {post.excerpt && <p className="mt-6 text-[#475569] text-lg md:text-xl leading-[1.65] max-w-2xl">{post.excerpt}</p>}
          </div>
        </section>

        {post.cover_image_url && (
          <div className="container-ags max-w-5xl mx-auto -mt-1 mb-12">
            <img src={post.cover_image_url} alt={post.title} className="w-full aspect-video object-cover" />
          </div>
        )}

        {/* Body */}
        <section className="py-12 md:py-16">
          <article className="container-ags max-w-3xl mx-auto prose prose-lg prose-headings:font-heading prose-headings:font-extrabold prose-headings:tracking-[-0.025em] prose-headings:text-[#050B14] prose-a:text-[#0E2A57] prose-a:underline prose-a:decoration-[#2BB6E5] prose-a:decoration-2 prose-a:underline-offset-2 prose-strong:text-[#050B14] prose-blockquote:border-l-[3px] prose-blockquote:border-[#2BB6E5] prose-blockquote:not-italic prose-blockquote:font-heading prose-blockquote:text-[#0E2A57]"
                   dangerouslySetInnerHTML={{ __html: post.body || "" }} />
        </section>
      </main>
      <Footer />
    </div>
  );
}
